import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';

import { useUpdateBankingDataMutation } from 'api/main';
import {
    checkAcceptedFileTypes,
    formatIBANOnType,
    validateFileSize,
    validateSchema,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    CustomTextField,
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    InfoButton,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, FormLabel, Typography } from '@mui/material';

const bicValidationSchema = (value, helpers) => {
    if (value.trim() === '') {
        return helpers.error('string.empty');
    }

    if (value.length !== 8 && value.length !== 11) {
        return helpers.error('string.length');
    }

    if (!/^\w+$/g.test(value)) {
        return helpers.error('string.pattern');
    }

    return value;
};

const bicValidationMessages = {
    'string.empty': texts.SAISIR_LE_CHAMP,
    'string.pattern': texts.CODE_BIC_INVALID_CHARACTERS,
    'string.length': texts.BIC_FORMAT_CONTENT,
};

const RibReplaceDialog = ({ onClose, open, doc, bankingStatement }) => {
    const [form, setForm] = useState({ iban: '', rib: null, bic: '' });
    const [errors, setErrors] = useState({ iban: '', rib: '', bic: '' });
    const [replaceError, setReplaceError] = useState('');
    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);

    const [updateBankingData, { isLoading, isSuccess, error, isError, reset }] =
        useUpdateBankingDataMutation();

    const handleFileChange = useCallback(({ target: { files } }) => {
        const file = files[0];
        checkAcceptedFileTypes(
            file,
            () => {
                if (validateFileSize(file)) {
                    setFileTypeError(false);
                    setForm(prevState => ({ ...prevState, rib: file }));
                    setFileSizeError(false);
                } else {
                    setFileSizeError(true);
                }
            },
            () => {
                setForm(prevState => ({ ...prevState, rib: null }));
                setFileTypeError(true);
                setFileSizeError(false);
            }
        );
    }, []);
    const handleIBANChange = useCallback(e => {
        setForm(prevState => ({ ...prevState, iban: formatIBANOnType(e.target) }));
        setErrors({ iban: '', rib: '', bic: '' });
    }, []);
    const handleBICChange = useCallback(({ target }) => {
        setForm(prevState => ({
            ...prevState,
            bic: target.value ? target.value.toUpperCase().replace(/\s/g, '') : '',
        }));
    }, []);

    const schema = useMemo(
        () => ({
            rib: Joi.any().invalid(null).messages({ 'any.invalid': texts.PROVIDE_NEW_RIB }),
            bic: Joi.string().custom(bicValidationSchema).messages(bicValidationMessages),
            iban: Joi.string()
                .required()
                .replace(/\s/g, '')
                .invalid(bankingStatement?.bankAccountNumber || '')
                .messages({
                    'string.empty': texts.SAISIR_IBAN,
                    'any.invalid': texts.IBAN_IS_THE_SAME,
                }),
        }),
        [bankingStatement?.bankAccountNumber]
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({ iban: '', bic: '', rib: '' });
            setReplaceError('');
            updateBankingData({ ...form, id: bankingStatement?.id, ribId: doc.id, bic: form.bic });
        } else {
            setErrors(newErrors);
        }
    }, [form, updateBankingData, bankingStatement, doc, schema]);

    const handleClose = useCallback(() => {
        onClose();
        setForm({ iban: '', rib: null, bic: '' });
        setReplaceError('');
        setErrors({ iban: '', rib: '', bic: '' });
    }, [onClose]);

    useEffect(() => {
        if (isSuccess) {
            reset();
            handleClose();
        }
    }, [handleClose, isSuccess, reset]);

    useEffect(() => {
        if (isError) {
            if (error?.status === 400 || error?.status === 422) {
                setReplaceError(texts.IBAN_REPLACE_PROBLEM);
            } else if (error?.status === 415) {
                setFileSizeError(true);
            }
            reset();
        }
    }, [isError, error, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.RIB_REPLACEMENT} handleClose={handleClose} />
            <DialogContent>
                <Box mt="20px" mb={2}>
                    <FormLabel
                        sx={{ marginBottom: '8px', letterSpacing: '1.6px' }}
                        component="legend"
                    >
                        {texts.PROVIDE_NEW_RIB}
                    </FormLabel>
                    <Typography
                        sx={{
                            marginRight: 2,
                            textAlign: 'left',
                            marginBottom: '5px',
                            marginTop: 2,
                            maxWidth: '550px',
                        }}
                        variant="body2"
                    >{`${texts.UPLOADED_FILE_NAME} : ${form.rib?.name || texts.AUCUN}`}</Typography>
                    {fileTypeError && <FileErrorMessage variant="type" />}
                    {fileSizeError && <FileErrorMessage variant="size" />}
                    <FileInputButton
                        noMargin
                        showIcon
                        id="rib-doc"
                        label={texts.UPLOAD_FILE_BUTTON}
                        onFileChange={handleFileChange}
                    />
                    <ErrorMessage message={errors.rib} />
                </Box>
                <CustomTextField
                    label={texts.SUBMIT_NEW_IBAN}
                    onChange={handleIBANChange}
                    value={form.iban}
                    error={errors.iban}
                    showError
                    inputProps={{ maxLength: 42 }}
                />
                <CustomTextField
                    fullWidth
                    label={texts.BIC_ALT_LABEL}
                    onChange={handleBICChange}
                    value={form.bic}
                    error={errors.bic}
                    inputProps={{ maxLength: 11 }}
                    infoButton={
                        <InfoButton
                            disableMarginBottom
                            title={texts.BIC_FORMAT_TITLE}
                            content={texts.BIC_FORMAT_CONTENT}
                        />
                    }
                />
                <ErrorMessage message={replaceError} />
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton disabled={isLoading} onClick={handleSubmit} loading={isLoading}>
                    {texts.SAVE}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

RibReplaceDialog.propTypes = {
    onClose: PropTypes.func,
    open: PropTypes.bool,
    doc: PropTypes.object,
    bankingStatement: PropTypes.object,
};

export default RibReplaceDialog;
